/**
 * Small UI + visualization helpers for LUMEN
 */

import type { SceneFamily } from './types';

export function setProbeStatus(msg: string) {
  const el = document.getElementById('probe-status');
  if (el) el.textContent = msg;
}

export function showRecoveryButton(enabled: boolean) {
  const btn = document.getElementById('start-recovery') as HTMLButtonElement | null;
  if (btn) btn.disabled = !enabled;
}

export function updateLumenSummary(stats: {
  sessions: number;
  avgDeltaCV: number | null;
  bestScene: SceneFamily | null;
  bestDelta: number | null;
}) {
  const sess = document.getElementById('stat-sessions');
  const dcv = document.getElementById('stat-delta-cv');
  const best = document.getElementById('stat-best-scene');
  const bestD = document.getElementById('stat-best-delta');

  if (sess) sess.textContent = String(stats.sessions || '—');
  if (dcv) {
    dcv.textContent = stats.avgDeltaCV == null ? '—' : (stats.avgDeltaCV * 100).toFixed(1) + '%';
  }
  if (best) {
    best.textContent = stats.bestScene ? sceneName(stats.bestScene) : '—';
  }
  if (bestD && stats.bestDelta != null) {
    bestD.textContent = `Δ CV ${(stats.bestDelta * 100).toFixed(1)}%`;
  }
}

function sceneName(s: SceneFamily) {
  return s === 'canopy' ? 'Canopy' : s === 'ripples' ? 'Ripples' : 'Meadow';
}

export function renderHistoryChart(container: HTMLElement, history: Array<{ ts: number; scene: SceneFamily; deltaCV: number; lumen: number }>) {
  container.innerHTML = '';
  if (!history.length) {
    container.innerHTML = `<div class="text-forest-400 text-xs p-6">Complete a full cycle (baseline → clearing → recovery) to see your restoration history.</div>`;
    return;
  }

  const c = document.createElement('canvas');
  c.width = container.clientWidth || 720;
  c.height = 188;
  container.appendChild(c);

  const ctx = c.getContext('2d')!;
  const pad = 28;
  const w = c.width - pad * 2;
  const h = c.height - pad * 2;

  ctx.fillStyle = '#0a0f0b';
  ctx.fillRect(0, 0, c.width, c.height);

  // Axes
  ctx.strokeStyle = 'rgba(163,176,158,0.2)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, pad);
  ctx.lineTo(pad, c.height - pad);
  ctx.lineTo(c.width - pad + 4, c.height - pad);
  ctx.stroke();

  if (history.length < 2) return;

  const minL = Math.min(...history.map(h => h.lumen));
  const maxL = Math.max(...history.map(h => h.lumen));
  const range = Math.max(18, maxL - minL);

  // Plot line + points
  ctx.strokeStyle = '#c9a16b';
  ctx.lineWidth = 2.25;
  ctx.beginPath();

  history.forEach((pt, i) => {
    const x = pad + (i / (history.length - 1)) * w;
    const norm = (pt.lumen - minL) / range;
    const y = (c.height - pad) - norm * h;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // Points + labels
  history.forEach((pt, i) => {
    const x = pad + (i / (history.length - 1)) * w;
    const norm = (pt.lumen - minL) / range;
    const y = (c.height - pad) - norm * h;

    ctx.fillStyle = '#e8d9b8';
    ctx.beginPath();
    ctx.arc(x, y, 3.5, 0, Math.PI * 2);
    ctx.fill();

    // Tiny scene label
    ctx.fillStyle = 'rgba(163,176,158,0.65)';
    ctx.font = '10px ui-sans-serif';
    ctx.fillText(pt.scene[0].toUpperCase(), x - 3, c.height - pad + 14);
  });

  // Current lumen annotation
  const last = history[history.length - 1];
  ctx.fillStyle = '#c9a16b';
  ctx.font = '600 11px ui-sans-serif';
  ctx.fillText(`Last Lumen: ${last.lumen}`, c.width - pad - 92, pad - 6);
}

export function showTurnstileMessage(container: HTMLElement, msg: string) {
  container.innerHTML = `<div class="text-xs px-3 py-2 rounded-xl bg-forest-950 border border-white/10">${msg}</div>`;
}
